import React, { useState } from 'react';
import { toast } from 'sonner';
import QuestionGeneratorForm from '@/components/admin/QuestionGeneratorForm';
import GeneratedQuestionsList from '@/components/admin/GeneratedQuestionsList';
import QuestionManagementMenu from '@/components/admin/QuestionManagementMenu';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import VideoGenerationModal from '@/components/admin/VideoGenerationModal';
import {
  GeneratedQuestion,
  DifficultyLevel,
  generateQuestions,
  saveQuestions,
} from '@/services/questionGeneratorService';

// Params we need to remember between generate and save so the save call can
// reuse the same grade/subject/category/difficulty without re-asking the form.
interface GenerationContext {
  grade: string;
  subjectId: string;
  categoryId: string;
  difficulty: DifficultyLevel;
}

const AdminQuestionGenerator = () => {
  const [questions, setQuestions] = useState<GeneratedQuestion[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastContext, setLastContext] = useState<GenerationContext | null>(null);
  const [videoOpen, setVideoOpen] = useState(false);

  const handleGenerateQuestions = async (params: {
    grade: string;
    subjectId: string;
    categoryId: string;
    count: number;
    difficulty: DifficultyLevel;
    customPrompt?: string;
    generateWithTopic?: boolean;
  }) => {
    setIsGenerating(true);
    try {
      const results = await generateQuestions(
        params.grade,
        params.subjectId,
        params.categoryId,
        params.count,
        params.difficulty,
        params.customPrompt,
        params.generateWithTopic ?? true,
      );
      // Default-select all generated questions so the user can save with one click.
      setQuestions(results.map((q) => ({ ...q, selected: true })));
      setSelectAll(true);
      setLastContext({
        grade: params.grade,
        subjectId: params.subjectId,
        categoryId: params.categoryId,
        difficulty: params.difficulty,
      });
      toast.success(`Generated ${results.length} questions`);
    } catch (error: any) {
      console.error('Error generating questions:', error);
      toast.error(error?.message || 'Failed to generate questions');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleToggleSelectAll = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    setQuestions((prev) => prev.map((q) => ({ ...q, selected: newSelectAll })));
  };

  const handleToggleQuestion = (id: string) => {
    setQuestions((prev) =>
      prev.map((q) => (q.id === id ? { ...q, selected: !q.selected } : q)),
    );
  };

  const handleSetCorrectOption = (questionId: string, optionId: string) => {
    setQuestions((prev) =>
      prev.map((q) =>
        q.id === questionId
          ? {
              ...q,
              options: q.options.map((opt) => ({
                ...opt,
                isCorrect: opt.id === optionId,
              })),
            }
          : q,
      ),
    );
  };

  const handleSaveQuestions = async () => {
    if (!lastContext) {
      toast.error('Generate questions first before saving');
      return;
    }
    const selectedQuestions = questions.filter((q) => q.selected);
    if (selectedQuestions.length === 0) {
      toast.error('Select at least one question to save');
      return;
    }
    // Guard: every saved question must have one correct option.
    const missingCorrect = selectedQuestions.find(
      (q) => !q.options.some((o) => o.isCorrect),
    );
    if (missingCorrect) {
      toast.error('Each selected question needs a correct answer marked');
      return;
    }

    setIsSaving(true);
    try {
      const savedCount = await saveQuestions(
        lastContext.grade,
        lastContext.subjectId,
        lastContext.categoryId,
        selectedQuestions,
        lastContext.difficulty,
      );
      toast.success(`Saved ${savedCount} questions`);
      // Remove saved questions from the working list.
      setQuestions((prev) => prev.filter((q) => !q.selected));
      setSelectAll(false);
    } catch (error: any) {
      console.error('Error saving questions:', error);
      toast.error(error?.message || 'Failed to save questions');
    } finally {
      setIsSaving(false);
    }
  };

  const handleShowVideoModal = () => {
    const selected = questions.filter((q) => q.selected);
    if (selected.length === 0) {
      toast.error('Select at least one question to include in the video');
      return;
    }
    setVideoOpen(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex flex-col">
      <Header />
      <div className="flex-1 container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-6">AI Question Generator</h1>
        <QuestionManagementMenu />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <QuestionGeneratorForm
            onGenerateQuestions={handleGenerateQuestions}
            isGenerating={isGenerating}
          />
          <GeneratedQuestionsList
            questions={questions}
            selectAll={selectAll}
            onToggleSelectAll={handleToggleSelectAll}
            onToggleQuestion={handleToggleQuestion}
            onSetCorrectOption={handleSetCorrectOption}
            onSaveQuestions={handleSaveQuestions}
            onShowVideoModal={handleShowVideoModal}
            isSaving={isSaving}
          />
        </div>
      </div>
      <VideoGenerationModal
        open={videoOpen}
        onClose={() => setVideoOpen(false)}
        questions={questions.filter((q) => q.selected)}
      />
      <Footer />
    </div>
  );
};

export default AdminQuestionGenerator;
